import { FC, useRef, useState } from 'react'
import MenuThreeDotsIcon from 'src/assets/svg/menuThreeDots.svg'
import { ReactComponent as ArrowRightIcon } from 'src/assets/svg/arrow-right-line.svg'
import { ReactComponent as ReloadIcon } from 'src/assets/svg/Reload.svg'
import { Skeleton } from 'src/components/Skeleton'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { InboxAnalysisPage } from 'src/reactQuery/useGetInboxAnalysisPagesQuery'
import { PageCardFooter } from './PageCardFooter'
import { ProgressRing } from './ProgressRing'
import { SentimentBar } from './SentimentBar'
import { getPlatformIcon, getPlatformLabel } from './platformIcons'
import classes from './pageCard.module.scss'

interface PageCardProps {
  onDisconnect: () => void
  onOpen: () => void
  page: InboxAnalysisPage
}

/**
 * One connected page on the Inbox Analysis landing page.
 *
 *   header   platform glyphs · name (+ handle) · platform list · Open Analysis · ⋮
 *   stats    Total Messages / Chats / Users, stacked sentiment bar, positive ring
 *   footer   Top Operators, or Key Insights when no real operator is attributed
 *
 * The whole card opens the analysis on click (mouse convenience); the
 * "Open Analysis" button is the keyboard / screen-reader path.
 */
export const PageCard: FC<PageCardProps> = ({ onDisconnect, onOpen, page }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useOutsideClick({ onOutsideClick: () => setIsMenuOpen(false), ref: menuRef })

  const emptyReason =
    page.emptyReason ??
    (page.messages.total === 0 && page.topOperators.length === 0
      ? 'no_data'
      : null)

  const sentimentTotal =
    page.messages.positive + page.messages.neutral + page.messages.negative
  const positiveShare = sentimentTotal
    ? (page.messages.positive / sentimentTotal) * 100
    : 0

  const stats = [
    { label: 'Total Messages', value: page.messages.total },
    { label: 'Total Chats', value: page.conversations.total },
    { label: 'Total Users', value: page.users ?? page.conversations.total },
  ]

  return (
    <article
      aria-label={page.name}
      className={classes.pageCard}
      onClick={(event) => {
        const target = event.target as HTMLElement
        if (!target.closest('button, a, [data-card-menu]')) onOpen()
      }}
    >
      <header className={classes.header}>
        <div className={classes.platformIcons}>
          {page.platforms.map((platform) => (
            <span className={classes.platformIcon} key={platform}>
              {getPlatformIcon(platform)}
            </span>
          ))}
        </div>
        <div className={classes.titleBlock}>
          <h3 className={classes.pageName}>
            <span className={classes.pageNameText}>{page.name}</span>
            {page.handle && (
              <span className={classes.pageHandle}>{page.handle}</span>
            )}
          </h3>
          <span className={classes.platformList}>
            {page.platforms.map(getPlatformLabel).join(' · ')}
          </span>
        </div>

        <button className={classes.openButton} onClick={onOpen} type="button">
          Open Analysis
          <ArrowRightIcon aria-hidden="true" />
        </button>

        <div className={classes.menu} data-card-menu ref={menuRef}>
          <button
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            aria-label={`More actions for ${page.name}`}
            className={classes.menuButton}
            onClick={() => setIsMenuOpen((open) => !open)}
            type="button"
          >
            <img alt="" src={MenuThreeDotsIcon} />
          </button>
          {isMenuOpen && (
            <div className={classes.menuList} role="menu">
              <button
                className={classes.menuItem}
                onClick={() => {
                  setIsMenuOpen(false)
                  onDisconnect()
                }}
                role="menuitem"
                type="button"
              >
                Disconnect
              </button>
            </div>
          )}
        </div>
      </header>

      {emptyReason === 'analyzing' ? (
        <div className={classes.emptyState}>
          <span className={classes.emptyIconBox}>
            <ReloadIcon className={classes.emptyIcon} />
          </span>
          <span className={classes.emptyTitle}>We&apos;re Analyzing Data</span>
          <span className={classes.emptySubtitle}>
            Check back in a few hours
          </span>
        </div>
      ) : emptyReason === 'no_data' ? (
        <div className={classes.emptyState}>
          <span className={classes.emptyTitle}>No Conversations Found</span>
          <span className={classes.emptySubtitle}>
            Nothing in the selected date range
          </span>
        </div>
      ) : (
        <>
          <div className={classes.statsPanel}>
            <div className={classes.statsMain}>
              <dl className={classes.statsRow}>
                {stats.map((stat) => (
                  <div className={classes.stat} key={stat.label}>
                    <dt className={classes.statLabel}>{stat.label}</dt>
                    <dd className={classes.statValue}>
                      {stat.value.toLocaleString()}
                    </dd>
                  </div>
                ))}
              </dl>
              <SentimentBar
                negative={page.messages.negative}
                neutral={page.messages.neutral}
                positive={page.messages.positive}
              />
            </div>
            <ProgressRing caption="positive" percent={positiveShare} />
          </div>

          <PageCardFooter page={page} />
        </>
      )}
    </article>
  )
}

export const PageCardSkeleton: FC = () => (
  <div aria-hidden="true" className={classes.pageCard}>
    <div className={classes.header}>
      <Skeleton height={26} radius={6} width={26} />
      <div className={classes.titleBlock}>
        <Skeleton height={15} width={120} />
        <Skeleton height={12} width={160} />
      </div>
      <Skeleton height={36} radius={10} width={134} />
    </div>
    <div className={classes.statsPanel}>
      <div className={classes.statsMain}>
        <div className={classes.statsRow}>
          {Array.from({ length: 3 }).map((_, index) => (
            <div className={classes.stat} key={index}>
              <Skeleton height={11} width={70} />
              <Skeleton height={24} width={64} />
            </div>
          ))}
        </div>
        <Skeleton height={6} width="100%" />
        <Skeleton height={12} width="100%" />
      </div>
      <Skeleton circle width={84} />
    </div>
    <div className={classes.footerSection}>
      <Skeleton height={13} width={96} />
      {Array.from({ length: 3 }).map((_, index) => (
        <Skeleton height={20} key={index} width="100%" />
      ))}
    </div>
  </div>
)
